import React, { useState, useEffect } from "react";
import axios from "axios";
import API_CONFIG from "../API_CONFIG/API_CONFIG";
import { 
  FiUser, 
  FiMail, 
  FiBriefcase, 
  FiCalendar, 
  FiUsers,
  FiSave,
  FiX,
  FiEdit2,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiArrowLeft,
  FiClock,
  FiPhone,
  FiMapPin
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const EditUserProfile = ({ userId, onUpdateSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    fonction: "",
    dn: "",
    sexe: "",
    status: 0,
    adresse: "",
    bio: ""
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Charger les données actuelles au montage
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setFetching(true);
        const res = await axios.get(`${API_CONFIG.apiUrl}profilUser.php?userId=${userId}`);
        if (res.data.success) {
          setFormData(res.data.data);
        }
      } catch (err) {
        console.error("Erreur de chargement", err);
        setMessage({ type: "error", text: "Impossible de charger les données" });
      } finally {
        setFetching(false);
      }
    };
    if (userId) fetchUser();
  }, [userId]);

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    
    switch(name) {
      case 'nom':
        if (!value.trim()) newErrors.nom = 'Le nom est requis';
        else if (value.trim().length < 2) newErrors.nom = 'Le nom doit contenir au moins 2 caractères';
        else delete newErrors.nom;
        break;
      case 'prenom':
        if (!value.trim()) newErrors.prenom = 'Le prénom est requis';
        else if (value.trim().length < 2) newErrors.prenom = 'Le prénom doit contenir au moins 2 caractères';
        else delete newErrors.prenom;
        break;
      case 'email':
        if (!value.trim()) newErrors.email = 'L\'email est requis';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) newErrors.email = 'Email invalide';
        else delete newErrors.email;
        break;
      case 'telephone':
        if (value && !/^(\+243|0)[0-9]{9}$/.test(value)) {
          newErrors.telephone = 'Format invalide (+243 ou 0 suivi de 9 chiffres)';
        } else delete newErrors.telephone;
        break;
      case 'dn':
        if (value && new Date(value) > new Date()) {
          newErrors.dn = 'La date ne peut pas être dans le futur';
        } else delete newErrors.dn;
        break;
      default:
        delete newErrors[name];
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setFormData({ ...formData, [name]: val });
    
    if (touched[name]) {
      validateField(name, val);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({ ...touched, [name]: true });
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Valider tous les champs
    let isValid = true;
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
      if (!validateField(key, formData[key])) isValid = false;
    });
    setTouched(allTouched);
    
    if (!isValid) {
      setMessage({ type: "error", text: "Veuillez corriger les erreurs dans le formulaire" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const res = await axios.post(`${API_CONFIG.apiUrl}updateUser.php`, {
        id: userId,
        ...formData,
      });

      if (res.data.success) {
        setMessage({ type: "success", text: res.data.message || "Profil mis à jour avec succès!" });
        if (onUpdateSuccess) {
          setTimeout(() => onUpdateSuccess(), 1000);
        }
      } else {
        setMessage({ type: "error", text: res.data.message || "Erreur lors de la mise à jour" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Erreur serveur ou réseau. Veuillez réessayer." });
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .edit-profile-wrapper {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0e27 0%, #1a1a3e 50%, #0a0e27 100%);
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 20px;
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      position: relative;
      overflow: hidden;
    }

    /* Background animations */
    .bg-circles {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      overflow: hidden;
      z-index: 0;
    }

    .bg-circle {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.3;
      animation: floatCircle 20s ease-in-out infinite;
    }

    .bg-circle-1 {
      width: 400px;
      height: 400px;
      background: linear-gradient(135deg, #1e40af, #3b82f6);
      top: -150px;
      left: -150px;
    }

    .bg-circle-2 {
      width: 350px;
      height: 350px;
      background: linear-gradient(135deg, #7c3aed, #6366f1);
      bottom: -100px;
      right: -100px;
      animation-delay: -5s;
    }

    .bg-circle-3 {
      width: 250px;
      height: 250px;
      background: linear-gradient(135deg, #059669, #10b981);
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation-delay: -10s;
      opacity: 0.1;
    }

    @keyframes floatCircle {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(30px, -40px) scale(1.1); }
      50% { transform: translate(-20px, 20px) scale(0.9); }
      75% { transform: translate(40px, 30px) scale(1.05); }
    }

    /* Card principale */
    .edit-card {
      width: 100%;
      max-width: 750px;
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 32px;
      padding: 0;
      position: relative;
      z-index: 1;
      box-shadow: 
        0 30px 60px -20px rgba(0, 0, 0, 0.8),
        0 0 40px rgba(59, 130, 246, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
      overflow: hidden;
      animation: cardAppear 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes cardAppear {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(20px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    /* Header */
    .edit-header {
      padding: 30px 40px 20px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .edit-header-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
      display: flex;
      align-items: center;
      justify-content: center;
      color: #60a5fa;
      font-size: 24px;
      flex-shrink: 0;
    }

    .edit-header-content {
      flex: 1;
    }

    .edit-header-title {
      font-size: 1.3rem;
      font-weight: 700;
      color: #f1f5f9;
      margin-bottom: 2px;
    }

    .edit-header-subtitle {
      font-size: 0.85rem;
      color: #64748b;
    }

    .edit-header-actions {
      display: flex;
      gap: 8px;
      margin-left: auto;
    }

    .header-btn {
      padding: 8px 16px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.03);
      color: #94a3b8;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.85rem;
      font-family: 'Inter', sans-serif;
    }

    .header-btn:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #f1f5f9;
      border-color: rgba(255, 255, 255, 0.15);
    }

    .header-btn.cancel {
      color: #fca5a5;
    }

    .header-btn.cancel:hover {
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.2);
    }

    /* Corps du formulaire */
    .edit-body {
      padding: 30px 40px 40px;
    }

    /* Messages */
    .message-container {
      padding: 14px 18px;
      border-radius: 12px;
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      gap: 12px;
      animation: slideDown 0.3s ease;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .message-container.success {
      background: rgba(16, 185, 129, 0.1);
      border: 1px solid rgba(16, 185, 129, 0.2);
      color: #4ade80;
    }

    .message-container.error {
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.2);
      color: #fca5a5;
    }

    .message-icon {
      font-size: 20px;
      flex-shrink: 0;
    }

    .message-text {
      flex: 1;
      font-size: 0.9rem;
    }

    /* Loader de chargement */
    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      gap: 16px;
    }

    .loading-spinner {
      width: 48px;
      height: 48px;
      border: 3px solid rgba(59, 130, 246, 0.1);
      border-radius: 50%;
      border-top-color: #3b82f6;
      animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .loading-text {
      color: #64748b;
      font-size: 0.9rem;
    }

    /* Formulaire */
    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.8rem;
      font-weight: 500;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .form-label .required {
      color: #ef4444;
    }

    .form-label .optional {
      color: #475569;
      font-size: 0.7rem;
      font-weight: 400;
      text-transform: none;
    }

    .input-wrapper {
      position: relative;
    }

    .form-input {
      width: 100%;
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.04);
      border: 1.5px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      color: #f1f5f9;
      font-size: 0.95rem;
      font-family: 'Inter', sans-serif;
      transition: all 0.3s ease;
      outline: none;
    }

    .form-input:focus {
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(59, 130, 246, 0.4);
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.05);
    }

    .form-input.error {
      border-color: rgba(239, 68, 68, 0.4);
    }

    .form-input:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .form-input::placeholder {
      color: #475569;
    }

    .form-select {
      appearance: none;
      cursor: pointer;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      padding-right: 40px;
    }

    .form-select option {
      background: #1a1a3e;
      color: #f1f5f9;
    }

    .error-message {
      font-size: 0.75rem;
      color: #fca5a5;
      padding-left: 4px;
      animation: slideDown 0.2s ease;
    }

    .input-hint {
      font-size: 0.7rem;
      color: #475569;
      padding-left: 4px;
    }

    .field-icon {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: #475569;
      font-size: 18px;
      pointer-events: none;
    }

    /* Actions du formulaire */
    .form-actions {
      display: flex;
      gap: 12px;
      margin-top: 30px;
      padding-top: 24px;
      border-top: 1px solid rgba(255, 255, 255, 0.05);
    }

    .btn-primary {
      flex: 1;
      padding: 14px 24px;
      border: none;
      border-radius: 12px;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: white;
      font-weight: 600;
      font-size: 0.95rem;
      font-family: 'Inter', sans-serif;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
    }

    .btn-primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 25px rgba(37, 99, 235, 0.4);
      filter: brightness(1.1);
    }

    .btn-primary:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-secondary {
      padding: 14px 24px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.03);
      color: #94a3b8;
      font-weight: 500;
      font-size: 0.95rem;
      font-family: 'Inter', sans-serif;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.06);
      color: #f1f5f9;
    }

    .spinner {
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      border-top-color: white;
      animation: spin 0.8s linear infinite;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .edit-header {
        padding: 20px;
        flex-wrap: wrap;
      }

      .edit-header-actions {
        width: 100%;
        margin-left: 0;
      }

      .edit-body {
        padding: 20px;
      }

      .form-grid {
        grid-template-columns: 1fr;
        gap: 16px;
      }

      .form-group.full-width {
        grid-column: 1;
      }

      .form-actions {
        flex-direction: column;
      }

      .btn-primary, .btn-secondary {
        width: 100%;
      }
    }

    @media (max-width: 480px) {
      .edit-card {
        border-radius: 20px;
      }

      .edit-header-title {
        font-size: 1.1rem;
      }

      .header-btn {
        padding: 6px 12px;
        font-size: 0.75rem;
      }

      .form-input {
        padding: 10px 14px;
        font-size: 0.9rem;
      }
    }
  `;

  if (fetching) {
    return (
      <div className="edit-profile-wrapper">
        <style>{styles}</style>
        <div className="bg-circles">
          <div className="bg-circle bg-circle-1" />
          <div className="bg-circle bg-circle-2" />
          <div className="bg-circle bg-circle-3" />
        </div>
        <div className="edit-card">
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Chargement du profil...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-profile-wrapper">
      <style>{styles}</style>

      <div className="bg-circles">
        <div className="bg-circle bg-circle-1" />
        <div className="bg-circle bg-circle-2" />
        <div className="bg-circle bg-circle-3" />
      </div>

      <motion.div 
        className="edit-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="edit-header">
          <div className="edit-header-icon">
            <FiEdit2 />
          </div>
          <div className="edit-header-content">
            <div className="edit-header-title">Modifier le profil</div>
            <div className="edit-header-subtitle">
              Mettez à jour vos informations personnelles
            </div>
          </div>
          <div className="edit-header-actions">
            <button className="header-btn cancel" onClick={onCancel}>
              <FiX size={16} />
              Annuler
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="edit-body">
          {/* Messages */}
          <AnimatePresence>
            {message.text && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`message-container ${message.type}`}
              >
                <span className="message-icon">
                  {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
                </span>
                <span className="message-text">{message.text}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {/* Nom */}
              <div className="form-group">
                <label className="form-label">
                  <FiUser size={14} />
                  Nom <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    className={`form-input ${touched.nom && errors.nom ? 'error' : ''}`}
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Votre nom"
                    required
                  />
                </div>
                {touched.nom && errors.nom && (
                  <span className="error-message">{errors.nom}</span>
                )}
              </div>

              {/* Prénom */}
              <div className="form-group">
                <label className="form-label">
                  <FiUser size={14} />
                  Prénom <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    className={`form-input ${touched.prenom && errors.prenom ? 'error' : ''}`}
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Votre prénom"
                    required
                  />
                </div>
                {touched.prenom && errors.prenom && (
                  <span className="error-message">{errors.prenom}</span>
                )}
              </div>

              {/* Email */}
              <div className="form-group full-width">
                <label className="form-label">
                  <FiMail size={14} />
                  Email <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    className={`form-input ${touched.email && errors.email ? 'error' : ''}`}
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="votre@email.com"
                    required
                  />
                </div>
                {touched.email && errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              {/* Téléphone */}
           

              {/* Fonction */}
              <div className="form-group full-width">
                <label className="form-label">
                  <FiBriefcase size={14} />
                  Fonction
                </label>
                <div className="input-wrapper">
                  <input
                    className="form-input"
                    name="fonction"
                    value={formData.fonction || ''}
                    onChange={handleChange}
                    placeholder="Votre fonction"
                    disabled
                  />
                </div>
                <span className="input-hint">Ce champ est géré par l'administrateur</span>
              </div>

              {/* Date de naissance */}
              <div className="form-group">
                <label className="form-label">
                  <FiCalendar size={14} />
                  Date de naissance
                </label>
                <div className="input-wrapper">
                  <input
                    className={`form-input ${touched.dn && errors.dn ? 'error' : ''}`}
                    type="date"
                    name="dn"
                    value={formData.dn || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                {touched.dn && errors.dn && (
                  <span className="error-message">{errors.dn}</span>
                )}
              </div>

              {/* Sexe */}
              <div className="form-group">
                <label className="form-label">
                  <FiUsers size={14} />
                  Sexe
                </label>
                <div className="input-wrapper">
                  <select
                    className="form-input form-select"
                    name="sexe"
                    value={formData.sexe || ''}
                    onChange={handleChange}
                  >
                    <option value="">Sélectionner</option>
                    <option value="M">Homme</option>
                    <option value="F">Femme</option>
                  </select>
                </div>
              </div>

        

        

              {/* Statut (lecture seule) */}
              <div className="form-group full-width">
                <label className="form-label">
                  <FiShield size={14} />
                  Statut du compte
                </label>
                <div className="input-wrapper">
                  <input
                    className="form-input"
                    type="text"
                    value={formData.status === 1 ? 'Actif' : 'Inactif'}
                    disabled
                  />
                  <FiShield className="field-icon" style={{ color: formData.status === 1 ? '#4ade80' : '#fca5a5' }} />
                </div>
                <span className="input-hint">Le statut est géré par l'administrateur</span>
              </div>
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={onCancel}>
                <FiX size={18} />
                Annuler
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <FiSave size={18} />
                    Enregistrer les modifications
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default EditUserProfile;